import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { DensityName, DensityTokens, ThemeName, ThemePalette, TypographyTokens } from './tokens';
import { DENSITY, TYPOGRAPHY, getPalette } from './tokens';
import { useUserPrefs } from '../hooks/useUserPrefs';

const STORAGE_ONBOARDING = 'chancey_onboarding_complete';

/** `rr,gg,bb` for Ionic `--ion-background-color-rgb` (6-digit hex only) */
function hexToRgbCsv(hex: string): string | null {
  const n = hex.replace('#', '');
  if (n.length !== 6) return null;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  if ([r, g, b].some((x) => Number.isNaN(x))) return null;
  return `${r},${g},${b}`;
}

export interface ChanceyThemeValue {
  themeName: ThemeName;
  dark: boolean;
  densityName: DensityName;
  c: ThemePalette;
  typ: TypographyTokens;
  d: DensityTokens;
  setThemeName: (t: ThemeName) => void;
  setDark: (v: boolean) => void;
  setDensityName: (d: DensityName) => void;
  onboardingComplete: boolean;
  completeOnboarding: () => void;
  /**
   * `true` when the user re-triggered onboarding from Settings → "Replay intro".
   * Replay keeps existing user data intact; the reminders skip action returns
   * to the app instead of clearing notification prefs.
   */
  replayMode: boolean;
  resetOnboarding: () => void;
}

const ChanceyThemeContext = createContext<ChanceyThemeValue | null>(null);

export const ChanceyThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { prefs, setPartial } = useUserPrefs();
  // MVP: single theme (Sunrise). Density pinned to comfy. Only dark mode is user-facing.
  const themeName: ThemeName = 'sunrise';
  const densityName: DensityName = 'comfy';
  const { dark } = prefs;
  const setThemeName = useCallback((t: ThemeName) => setPartial({ themeName: t }), [setPartial]);
  const setDark = useCallback((v: boolean) => setPartial({ dark: v }), [setPartial]);
  const setDensityName = useCallback(
    (d: DensityName) => setPartial({ densityName: d }),
    [setPartial]
  );
  const [onboardingComplete, setOnboardingComplete] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_ONBOARDING) === '1';
    } catch {
      return false;
    }
  });
  const [replayMode, setReplayMode] = useState(false);

  const c = useMemo(() => getPalette(themeName, dark), [themeName, dark]);
  const typ = useMemo(() => TYPOGRAPHY[themeName], [themeName]);
  const d = useMemo(() => DENSITY[densityName], [densityName]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--font-display', typ.display);
    root.style.setProperty('--font-body', typ.body);
    root.style.setProperty('--font-mono', typ.mono);
    root.style.setProperty('--chancey-bg', c.bg);
    root.style.setProperty('--chancey-ink', c.ink);
    root.style.setProperty('--chancey-accent', c.accent);

    // Override Ionic defaults (including prefers-color-scheme dark in variables.css)
    root.style.setProperty('--ion-background-color', c.bg);
    root.style.setProperty('--ion-text-color', c.ink);
    const bgRgb = hexToRgbCsv(c.bg);
    const inkRgb = hexToRgbCsv(c.ink);
    if (bgRgb) root.style.setProperty('--ion-background-color-rgb', bgRgb);
    if (inkRgb) root.style.setProperty('--ion-text-color-rgb', inkRgb);

    document.body.style.backgroundColor = c.bg;
    document.body.style.setProperty('--ion-background-color', c.bg);
    document.body.style.setProperty('--ion-text-color', c.ink);
    if (bgRgb) document.body.style.setProperty('--ion-background-color-rgb', bgRgb);
    if (inkRgb) document.body.style.setProperty('--ion-text-color-rgb', inkRgb);

    root.style.colorScheme = dark ? 'dark' : 'light';
  }, [typ, c, dark]);

  const completeOnboarding = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_ONBOARDING, '1');
    } catch {
      /* ignore */
    }
    setReplayMode(false);
    setOnboardingComplete(true);
  }, []);

  /**
   * Re-trigger the onboarding flow. Intentionally does NOT clear
   * `useUserPrefs` state (game selection, notification preferences) —
   * replay is strictly educational so a user with existing tickets doesn't
   * accidentally switch games and strand their scanned-ticket history.
   * OnboardingPage reads `replayMode` to make skip actions non-destructive.
   */
  const resetOnboarding = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_ONBOARDING);
    } catch {
      /* ignore */
    }
    setReplayMode(true);
    setOnboardingComplete(false);
  }, []);

  const value = useMemo<ChanceyThemeValue>(
    () => ({
      themeName,
      dark,
      densityName,
      c,
      typ,
      d,
      setThemeName,
      setDark,
      setDensityName,
      onboardingComplete,
      completeOnboarding,
      replayMode,
      resetOnboarding,
    }),
    [
      themeName,
      dark,
      densityName,
      c,
      typ,
      d,
      setThemeName,
      setDark,
      setDensityName,
      onboardingComplete,
      completeOnboarding,
      replayMode,
      resetOnboarding,
    ]
  );

  return (
    <ChanceyThemeContext.Provider value={value}>{children}</ChanceyThemeContext.Provider>
  );
};

export function useChanceyTheme(): ChanceyThemeValue {
  const ctx = useContext(ChanceyThemeContext);
  if (!ctx) {
    throw new Error('useChanceyTheme must be used within ChanceyThemeProvider');
  }
  return ctx;
}
